// The RemindLoop mark: three misregistered rounded plates (rx=9 on a 48
// viewBox) -- a pink plate peeking top-left, a cyan plate peeking
// bottom-right, both behind a navy plate on multiply blend (so the
// navy/cyan overlap darkens like wet ink) -- holding a paper-white serif
// R. See design_handoff_remindloop_site/README.md's "Assets" section;
// the multiply blend is load-bearing, a flat navy plate loses the print
// reference.
//
// The pink plate was added 2026-09-23, replacing a brief "Pink R"
// (magenta letterform) variant the user had rejected on sight -- this
// version was approved from a real mockup first (both the plate's
// position and this exact shade) before shipping. Pink is magenta-500
// (`#ff458e`) even on the light plate/ground, by explicit user choice
// "in favour of the other magentas" -- normally magenta-500 fails text
// contrast on the pale ground (see the wordmark's own light/dark step-
// down below), but that finding is about *readable text*, not a small
// decorative accent plate, so it doesn't apply here.
export function LogoMark({ size = 24, reversed = false }: { size?: number; reversed?: boolean }) {
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" aria-hidden="true" style={{ display: 'block', flexShrink: 0 }}>
      <rect x="8" y="7" width="37" height="37" rx="9" fill="#00c8ee" />
      <rect x="0" y="1" width="37" height="37" rx="9" fill="#ff458e" />
      <rect
        x="3"
        y="4"
        width="37"
        height="37"
        rx="9"
        fill={reversed ? '#ffffff' : '#000080'}
        opacity={reversed ? 0.92 : 1}
        style={reversed ? undefined : { mixBlendMode: 'multiply' }}
      />
      <text
        x="21.5"
        y="31"
        textAnchor="middle"
        fontFamily="'Source Serif 4', Georgia, serif"
        fontSize="25"
        fontWeight={600}
        fill={reversed ? '#000080' : '#ffffff'}
      >
        R
      </text>
    </svg>
  )
}

// Wordmark is two-tone: "Remind" in navy carries brand hierarchy, "Loop"
// in fuchsia -- the one sanctioned exception to pink being the CTA color
// only. "Loop" is a flat magenta-500 in both light and dark mode as of
// 2026-09-24 -- explicit user choice, made after seeing the tradeoff
// directly (a side-by-side mockup with real measured contrast numbers):
// this matches the logo mark's own pink accent plate exactly on a light
// ground, at the cost of "Loop" measuring ~3.0:1 contrast there (below
// the 4.5:1 text-readability guideline; magenta-700, the previous light-
// mode step, measured ~6.4:1). Dark mode is unaffected -- it was already
// magenta-500.
export function Logo({ size = 24, reversed = false }: { size?: number; reversed?: boolean }) {
  // Wordmark text scales proportionally with the icon (17px at the
  // size=24 default, the MarketingHeader.tsx original) rather than a
  // fixed 17px regardless of `size` -- a larger icon next to fixed-size
  // text looked mismatched the one time this was tried at a non-default
  // size (auth pages' centered logo, see Login.tsx and its siblings).
  const textSizePx = Math.round((size / 24) * 17)
  return (
    <span className="flex items-center gap-2.5 font-semibold" style={{ fontSize: textSizePx }}>
      <LogoMark size={size} reversed={reversed} />
      <span>
        <span className={reversed ? 'text-white' : 'text-navy-700'}>Remind</span>
        <span className="text-magenta-500">Loop</span>
      </span>
    </span>
  )
}
